import { useState, useRef, useCallback, useEffect } from 'react'
import { Save, RotateCcw, Settings, Send } from 'lucide-react'
import { ConfigEditor, ConfigEditorHandle } from '@/components/config/ConfigEditor'
import { configApi } from '@/services/api/config'
import { useAuthStore } from '@/store'
import { extractErrorMessage } from '@/utils/errorHandler'

export function EmailSettingsSection() {
  const editorRef = useRef<ConfigEditorHandle>(null)
  const successTimeoutRef = useRef<number | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [configLabel, setConfigLabel] = useState('E-Mail')
  const [configDescription, setConfigDescription] = useState('')
  const userEmail = useAuthStore(s => s.user?.email)

  const handleConfigLoaded = useCallback((label: string, description: string) => {
    setConfigLabel(label)
    setConfigDescription(description)
  }, [])

  const handleError = useCallback((error: string | null) => {
    setError(error)
  }, [])

  const showSuccess = useCallback((message: string) => {
    setSuccess(message)
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current)
    }
    successTimeoutRef.current = setTimeout(() => setSuccess(null), 5000)
  }, [])

  const handleSuccess = useCallback(() => {
    showSuccess('Konfiguration gespeichert')
  }, [showSuccess])

  const handleDirtyChange = useCallback((isDirty: boolean) => {
    setDirty(isDirty)
  }, [])

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current)
      }
    }
  }, [])

  const handleSave = async () => {
    if (!editorRef.current?.isDirty()) return
    setSaving(true)
    await editorRef.current.save()
    setSaving(false)
  }

  const handleReset = () => {
    editorRef.current?.reset()
  }

  const handleTestEmail = async () => {
    if (dirty || testing) return
    setTesting(true)
    setError(null)
    try {
      const result = await configApi.testEmail()
      showSuccess(result.message)
    } catch (err) {
      setError(extractErrorMessage(err, {
        loadError: 'Test-E-Mail konnte nicht gesendet werden',
      }))
    } finally {
      setTesting(false)
    }
  }

  return (
    <div className="flex flex-col min-h-full">
      <div className="px-4 lg:px-6 py-3 border-b bg-white flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
            <Settings className="w-5 h-5 text-indigo-600" />
          </div>
          <h1 className="text-lg font-bold">{configLabel}</h1>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <button type="button" onClick={handleReset} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 py-1">
              <RotateCcw className="w-3.5 h-3.5" /> Verwerfen
            </button>
          )}
          <button
            type="button"
            onClick={handleTestEmail}
            disabled={testing || dirty}
            title={dirty
              ? 'Bitte zuerst speichern'
              : (userEmail ? `Test-E-Mail an ${userEmail} senden` : 'Test-E-Mail an Superadmin senden')}
            className="px-3 py-1.5 text-sm border border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50 flex items-center gap-1.5 disabled:opacity-50"
          >
            <Send className="w-4 h-4" /> {testing ? 'Senden...' : 'Test-E-Mail'}
          </button>
          <button type="button" onClick={handleSave} disabled={saving || !dirty}
            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-1.5 disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>

      {configDescription && (
        <div className="px-4 lg:px-6 py-3 bg-white border-b">
          <p className="text-sm text-gray-600">{configDescription}</p>
        </div>
      )}

      {error && <div className="bg-red-50 px-4 lg:px-6 py-3 text-sm text-red-700">{error}</div>}
      {success && <div className="bg-green-50 px-4 lg:px-6 py-3 text-sm text-green-700">{success}</div>}

      <ConfigEditor
        ref={editorRef}
        configType="email"
        onDirtyChange={handleDirtyChange}
        onError={handleError}
        onSuccess={handleSuccess}
        onConfigLoaded={handleConfigLoaded}
      />
    </div>
  )
}
